/**
 * #441: Statements list -> Statement view, on the Reconciliation page.
 *
 * List: one row per uploaded statement. View: the policy lines of one
 * statement, grouped by result with counts the SERVER computes (never counted
 * in the browser), filters that live in the URL, 100 lines a page, and the
 * line drawer for decisions and disputes. A broker admin acts; a platform
 * admin reads (no control is rendered for them).
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Badge, Button, Card, Pagination, Select } from '@/components/common';
import { useInsurersLookup } from '@/hooks/useInsurers';
import { getErrorMessage } from '@/services';
import { statementVerificationService } from '@/services/statementVerification';
import type {
  DisputeAction,
  NettedRow,
  StatementBatch,
  StatementCategory,
  StatementPolicyLine,
} from '@/services/statementVerification';
import { formatCurrency, formatDate } from '@/utils';
import { StatementExportDialog } from './StatementExportDialog';
import { StatementLineDrawer } from './StatementLineDrawer';
import {
  ACTIONS_WITH_AMOUNT,
  ACTION_LABELS,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  CATEGORY_VARIANT,
  DECISION_LABELS,
  DISPUTE_LABELS,
  DISPUTE_ORDER,
  FLAG_LABELS,
  KIND_BADGE,
  flagLabel,
  periodLabel,
} from './statementLabels';

const money = (v: string | null | undefined) => (v == null || v === '' ? '—' : formatCurrency(v));
const num = (v: string | null | undefined) => (v == null || v === '' ? null : Number(v));

function diffClass(v: string | null) {
  const n = num(v);
  if (n === null || n === 0) return 'text-gray-500 dark:text-gray-400';
  return n < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400';
}

function ErrorBox({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm dark:border-red-900 dark:bg-red-950/30">
      <p className="font-medium text-red-800 dark:text-red-300">Could not load this</p>
      <p className="mt-1 text-red-700 dark:text-red-400">{getErrorMessage(error)}</p>
      <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-2 p-4" aria-label="Loading">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-10 rounded bg-gray-100 dark:bg-gray-700" />
      ))}
    </div>
  );
}

// ─── Statements list ───────────────────────────────────────────────────────────

function StatementsList({
  canAct,
  onOpen,
  onUpload,
}: {
  canAct: boolean;
  onOpen: (b: StatementBatch) => void;
  onUpload: () => void;
}) {
  const [params, setParams] = useSearchParams();
  const insurer = params.get('insurer') ?? '';
  const month = params.get('month') ?? '';
  const status = params.get('bstatus') ?? 'current';
  const page = Number(params.get('bpage') ?? '1') || 1;
  const insurers = useInsurersLookup();

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k !== 'bpage') next.delete('bpage');
    setParams(next, { replace: true });
  };

  const q = useQuery({
    queryKey: ['statementBatches', insurer, month, status, page],
    queryFn: () => statementVerificationService.listBatches({ insurer, period: month, status, page }),
    placeholderData: (prev) => prev,
  });
  const rows = q.data?.results ?? [];

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 p-4 dark:border-gray-700">
        <div className="w-56">
          <Select
            id="stmt-insurer"
            label="Insurance company"
            value={insurer}
            onChange={(e) => set('insurer', e.target.value)}
            options={[
              { value: '', label: 'All companies' },
              ...(insurers.data ?? []).map((i) => ({ value: i.id, label: i.name })),
            ]}
          />
        </div>
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Statement month
          <input
            type="month"
            value={month}
            onChange={(e) => set('month', e.target.value)}
            className="mt-1 block rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          />
        </label>
        <div className="w-44">
          <Select
            id="stmt-show"
            label="Show"
            value={status}
            onChange={(e) => set('bstatus', e.target.value)}
            options={[
              { value: 'current', label: 'Current statements' },
              { value: 'superseded', label: 'Replaced statements' },
              { value: 'all', label: 'All' },
            ]}
          />
        </div>
        {canAct && (
          <Button className="ml-auto" onClick={onUpload}>
            Upload a statement
          </Button>
        )}
      </div>

      {q.isLoading ? (
        <Skeleton />
      ) : q.isError ? (
        <div className="p-4">
          <ErrorBox error={q.error} onRetry={() => q.refetch()} />
        </div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          <p className="font-medium text-gray-900 dark:text-gray-100">No statements yet.</p>
          <p className="mt-1">{canAct ? 'Upload one to check it against your books.' : 'Nothing has been uploaded.'}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                <th className="sticky left-0 bg-gray-50 px-4 py-2 dark:bg-gray-800">Insurer</th>
                <th className="px-4 py-2">Period</th>
                <th className="px-4 py-2">File</th>
                <th className="px-4 py-2">Policies</th>
                <th className="px-4 py-2">Results</th>
                <th className="px-4 py-2 text-right">Statement</th>
                <th className="px-4 py-2 text-right">Our books</th>
                <th className="px-4 py-2 text-right">Difference</th>
                <th className="px-4 py-2 text-right">Open disputes</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((b) => (
                <tr key={b.id} className={b.superseded_by ? 'text-gray-400 line-through' : ''}>
                  <td className="sticky left-0 bg-white px-4 py-2 font-medium dark:bg-gray-900">{b.insurer_name}</td>
                  <td className="px-4 py-2">{periodLabel(b.period)}</td>
                  <td className="px-4 py-2">
                    <div>{b.source_file}</div>
                    <div className="text-xs text-gray-500">
                      {b.uploaded_by_name} · {formatDate(b.created_at)}
                    </div>
                  </td>
                  <td className="px-4 py-2">{b.policy_line_count}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {CATEGORY_ORDER.filter((c) => b.totals.categories[c]).map((c) => (
                        <Badge key={c} variant={CATEGORY_VARIANT[c]} size="sm">
                          {CATEGORY_LABELS[c]} {b.totals.categories[c]}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right">{money(b.totals.stated_total)}</td>
                  <td className="px-4 py-2 text-right">{money(b.totals.expected_total)}</td>
                  <td className={`px-4 py-2 text-right ${diffClass(b.totals.difference)}`}>{money(b.totals.difference)}</td>
                  <td className="px-4 py-2 text-right">{b.totals.open_disputes}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => onOpen(b)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && q.data.count > 20 && (
        <Pagination
          currentPage={page}
          totalPages={Math.ceil(q.data.count / 20)}
          totalItems={q.data.count}
          itemsPerPage={20}
          onPageChange={(p) => set('bpage', String(p))}
        />
      )}
    </Card>
  );
}

// ─── Statement view ────────────────────────────────────────────────────────────

function NettedRows({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['statementLineRows', id], queryFn: () => statementVerificationService.lineRows(id) });
  if (q.isLoading) return <p className="p-2 text-xs text-gray-500">Loading rows…</p>;
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const rows: NettedRow[] = q.data ?? [];
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-gray-500">
        <tr>
          <th className="px-2 py-1">Row</th>
          <th className="px-2 py-1">Kind</th>
          <th className="px-2 py-1">Refs</th>
          <th className="px-2 py-1">Date</th>
          <th className="px-2 py-1 text-right">Premium</th>
          <th className="px-2 py-1 text-right">Commission</th>
          <th className="px-2 py-1 text-right">Reward</th>
          <th className="px-2 py-1 text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td className="px-2 py-1">{r.row_number ?? '—'}</td>
            <td className="px-2 py-1">{r.kind || '—'}</td>
            <td className="px-2 py-1">{[r.endorsement_ref, r.transaction_ref].filter(Boolean).join(' / ') || '—'}</td>
            <td className="px-2 py-1">{formatDate(r.date)}</td>
            <td className="px-2 py-1 text-right">{money(r.premium)}</td>
            <td className="px-2 py-1 text-right">{money(r.commission)}</td>
            <td className="px-2 py-1 text-right">{money(r.reward)}</td>
            <td className="px-2 py-1 text-right">{money(r.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RateCell({ line }: { line: StatementPolicyLine }) {
  const link = line.rate_link;
  const stated = line.stated_rate ? `${Number(line.stated_rate)}%` : '—';
  const ours = line.expected_rate ? `${Number(line.expected_rate)}%` : '—';
  let source = null;
  if (link?.grid_version) {
    source = (
      <Link className="text-primary-600 underline" to={`/commissions/grid-versions/${link.grid_version}`}>
        Grid row
      </Link>
    );
  } else if (link?.rate_id) {
    source = <span className="text-gray-500">Broker rate #{link.rate_id.slice(0, 8)}</span>;
  }
  return (
    <div>
      <div>
        {stated} / {ours}
      </div>
      <div className="text-xs">{source}</div>
    </div>
  );
}

function StatementView({
  batch,
  canAct,
  onBack,
  onUpload,
}: {
  batch: StatementBatch;
  canAct: boolean;
  onBack: () => void;
  onUpload: () => void;
}) {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const cat = params.get('cat') ?? '';
  const flag = params.get('flag') ?? '';
  const dec = params.get('dec') ?? '';
  const disp = params.get('disp') ?? '';
  const search = params.get('q') ?? '';
  const sort = params.get('sort') ?? 'base_policy_number';
  const page = Number(params.get('page') ?? '1') || 1;

  const [open, setOpen] = useState<StatementPolicyLine | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exportOpen, setExportOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<DisputeAction | 'open' | ''>('');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkAmount, setBulkAmount] = useState('');

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k !== 'page') next.delete('page');
    setParams(next, { replace: true });
    setSelected(new Set());
  };

  const filters = useMemo(
    () => ({ batch: batch.id, category: cat, flag, decision: dec, dispute_status: disp, search }),
    [batch.id, cat, flag, dec, disp, search],
  );

  const lines = useQuery({
    queryKey: ['statementLines', filters, sort, page],
    queryFn: () => statementVerificationService.listLines({ ...filters, ordering: sort, page }),
    placeholderData: (prev) => prev,
  });
  // Counts for the badges come from the server and ignore the category filter.
  const summary = useQuery({
    queryKey: ['statementLineSummary', { ...filters, category: '' }],
    queryFn: () => statementVerificationService.lineSummary({ ...filters, category: '' }),
  });
  const disputes = useQuery({
    queryKey: ['statementDisputeSummary', batch.id],
    queryFn: () => statementVerificationService.disputeSummary({ batch: batch.id }),
  });

  const recheck = useMutation({
    mutationFn: () => statementVerificationService.recheckBatch(batch.id),
    onSuccess: () => {
      toast.success('Re-checked. Decisions and disputes were kept.');
      qc.invalidateQueries({ queryKey: ['statementLines'] });
      qc.invalidateQueries({ queryKey: ['statementLineSummary'] });
      qc.invalidateQueries({ queryKey: ['statementBatches'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const bulk = useMutation({
    mutationFn: () =>
      statementVerificationService.bulk({
        policy_line_ids: [...selected],
        action: bulkAction as DisputeAction | 'open',
        note: bulkNote,
        amount_settled:
          bulkTakesAmount && bulkAmount.trim() !== '' ? bulkAmount.trim() : undefined,
      }),
    onSuccess: (r) => {
      const msg = `${r.succeeded} changed${r.failed ? `, ${r.failed} could not be changed` : ''}`;
      (r.failed ? toast.error : toast.success)(msg);
      if (r.failed) {
        const reasons = [...new Set(r.results.filter((x) => !x.ok && x.reason).map((x) => x.reason))];
        if (reasons.length) toast.error(reasons.join(' / '));
      }
      setSelected(new Set());
      setBulkAction('');
      setBulkNote('');
      setBulkAmount('');
      qc.invalidateQueries({ queryKey: ['statementLines'] });
      qc.invalidateQueries({ queryKey: ['statementDisputeSummary'] });
      qc.invalidateQueries({ queryKey: ['statementBatches'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const rows = lines.data?.results ?? [];
  const total = lines.data?.count ?? 0;
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const sortBy = (field: string) => set('sort', sort === field ? `-${field}` : field);
  const bulkNeedsNote = bulkAction !== '' && bulkAction !== 'open';
  // The backend takes one amount and applies it to every selected line; it is
  // required for resolve and write off, optional for insurer agreed.
  const bulkTakesAmount = bulkAction !== '' && bulkAction !== 'open' && ACTIONS_WITH_AMOUNT.includes(bulkAction);
  const bulkAmountRequired = bulkAction === 'resolve' || bulkAction === 'write_off';
  const bulkAmountText = bulkAmount.trim();
  const bulkAmountValid =
    bulkAmountText === ''
      ? !bulkAmountRequired
      : /^\d{1,10}(\.\d{1,2})?$/.test(bulkAmountText);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          Back to statements
        </Button>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {batch.insurer_name} · {periodLabel(batch.period)}
          </h3>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {batch.source_file}
            {batch.tolerance_used ? ` · tolerance ${money(batch.tolerance_used)}` : ''}
            {batch.superseded_by ? ' · replaced by a later upload' : ''}
          </p>
        </div>
        {canAct && (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={onUpload}>
              Upload
            </Button>
            <Button size="sm" variant="outline" disabled={recheck.isPending} onClick={() => recheck.mutate()}>
              {recheck.isPending ? 'Re-checking…' : 'Re-check'}
            </Button>
            <Button size="sm" onClick={() => setExportOpen(true)}>
              Export disputes
            </Button>
          </div>
        )}
      </div>

      <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Results">
        <button
          type="button"
          aria-pressed={cat === ''}
          onClick={() => set('cat', '')}
          className={`rounded-full border px-3 py-1 text-sm ${cat === '' ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600'}`}
        >
          All {summary.data?.total_count ?? ''}
        </button>
        {CATEGORY_ORDER.map((c: StatementCategory) => (
          <button
            key={c}
            type="button"
            aria-pressed={cat === c}
            onClick={() => set('cat', cat === c ? '' : c)}
            className={`rounded-full border px-3 py-1 text-sm ${cat === c ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600'}`}
          >
            {CATEGORY_LABELS[c]} {summary.data?.categories[c]?.count ?? ''}
          </button>
        ))}
      </div>

      {disputes.data && disputes.data.total_count > 0 && (
        <Card className="mb-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm" aria-label="Disputes">
            {DISPUTE_ORDER.map((s) => (
              <span key={s}>
                {DISPUTE_LABELS[s]}: <strong>{disputes.data!.by_status[s]?.count ?? 0}</strong>
              </span>
            ))}
            <span className="ml-auto">
              Disputed {money(disputes.data.disputed)} · Recovered {money(disputes.data.recovered)}
            </span>
          </div>
        </Card>
      )}

      <Card padding="none">
        <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 p-4 dark:border-gray-700">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            Policy number
            <input
              type="search"
              value={search}
              onChange={(e) => set('q', e.target.value)}
              className="mt-1 block rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            />
          </label>
          <div className="w-44">
            <Select
              id="stmt-flag"
            label="Flag"
              value={flag}
              onChange={(e) => set('flag', e.target.value)}
              options={[{ value: '', label: 'Any flag' }, ...Object.entries(FLAG_LABELS).map(([value, label]) => ({ value, label }))]}
            />
          </div>
          <div className="w-44">
            <Select
              id="stmt-decision"
            label="Decision"
              value={dec}
              onChange={(e) => set('dec', e.target.value)}
              options={[
                { value: '', label: 'Any decision' },
                ...Object.entries(DECISION_LABELS).map(([value, label]) => ({ value, label })),
              ]}
            />
          </div>
          <div className="w-48">
            <Select
              id="stmt-dispute"
            label="Dispute"
              value={disp}
              onChange={(e) => set('disp', e.target.value)}
              options={[
                { value: '', label: 'Any' },
                { value: 'none', label: 'No dispute' },
                ...DISPUTE_ORDER.map((s) => ({ value: s, label: DISPUTE_LABELS[s] })),
              ]}
            />
          </div>
        </div>

        {canAct && selected.size > 0 && (
          <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 bg-primary-50 p-3 text-sm dark:border-gray-700 dark:bg-primary-900/10">
            <strong>{selected.size} selected</strong>
            <div className="w-56">
              <Select
                id="stmt-bulk-action"
            label="Change status"
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value as DisputeAction | 'open' | '')}
                options={[
                  { value: '', label: 'Choose…' },
                  { value: 'open', label: 'Start a dispute' },
                  ...(Object.keys(ACTION_LABELS) as DisputeAction[]).map((a) => ({ value: a, label: ACTION_LABELS[a] })),
                ]}
              />
            </div>
            {bulkNeedsNote && (
              <label className="flex-1">
                Note (required)
                <input
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                  className="mt-1 block w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                />
              </label>
            )}
            {bulkTakesAmount && (
              <label className="w-48">
                Amount settled per line{bulkAmountRequired ? ' (required)' : ''}
                <input
                  inputMode="decimal"
                  value={bulkAmount}
                  onChange={(e) => setBulkAmount(e.target.value)}
                  aria-invalid={!bulkAmountValid}
                  className="mt-1 block w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                />
                {!bulkAmountValid && bulkAmountText !== '' && (
                  <span className="text-xs text-red-600">A non-negative amount, at most 2 decimals.</span>
                )}
              </label>
            )}
            <Button
              size="sm"
              disabled={
                bulkAction === '' ||
                (bulkNeedsNote && !bulkNote.trim()) ||
                !bulkAmountValid ||
                bulk.isPending
              }
              onClick={() => bulk.mutate()}
            >
              Apply
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        )}

        {lines.isLoading ? (
          <Skeleton />
        ) : lines.isError ? (
          <div className="p-4">
            <ErrorBox error={lines.error} onRetry={() => lines.refetch()} />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">
            No lines match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                <tr>
                  {canAct && (
                    <th className="px-3 py-2">
                      <input type="checkbox" aria-label="Select all lines on this page" checked={allSelected} onChange={toggleAll} />
                    </th>
                  )}
                  <th className="sticky left-0 bg-gray-50 px-3 py-2 dark:bg-gray-800">Policy</th>
                  <th className="px-3 py-2">Result</th>
                  <th className="px-3 py-2 text-right">Premium (stmt / ours)</th>
                  <th className="px-3 py-2">Rate (stmt / ours)</th>
                  <th className="px-3 py-2 text-right">Amount stmt</th>
                  <th className="px-3 py-2 text-right">Amount ours</th>
                  <th className="px-3 py-2 text-right">
                    <button type="button" onClick={() => sortBy('difference')} className="uppercase">
                      Difference {sort === 'difference' ? '▲' : sort === '-difference' ? '▼' : ''}
                    </button>
                  </th>
                  <th className="px-3 py-2">Flags</th>
                  <th className="px-3 py-2">Decision</th>
                  <th className="px-3 py-2">Dispute</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((l) => (
                  <RowGroup
                    key={l.id}
                    line={l}
                    canAct={canAct}
                    checked={selected.has(l.id)}
                    expanded={Boolean(expanded[l.id])}
                    onCheck={() => toggle(l.id)}
                    onExpand={() => setExpanded((e) => ({ ...e, [l.id]: !e[l.id] }))}
                    onOpen={() => setOpen(l)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > 100 && (
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(total / 100)}
            totalItems={total}
            itemsPerPage={100}
            onPageChange={(p) => set('page', String(p))}
          />
        )}
      </Card>

      <StatementLineDrawer
        line={open ? (rows.find((r) => r.id === open.id) ?? open) : null}
        canAct={canAct}
        onClose={() => setOpen(null)}
      />
      {canAct && (
        <StatementExportDialog
          isOpen={exportOpen}
          onClose={() => setExportOpen(false)}
          insurer={batch.insurer}
          batch={batch.id}
        />
      )}
    </div>
  );
}

function RowGroup({
  line,
  canAct,
  checked,
  expanded,
  onCheck,
  onExpand,
  onOpen,
}: {
  line: StatementPolicyLine;
  canAct: boolean;
  checked: boolean;
  expanded: boolean;
  onCheck: () => void;
  onExpand: () => void;
  onOpen: () => void;
}) {
  const cols = canAct ? 12 : 11;
  return (
    <>
      <tr>
        {canAct && (
          <td className="px-3 py-2">
            <input type="checkbox" aria-label={`Select ${line.base_policy_number}`} checked={checked} onChange={onCheck} />
          </td>
        )}
        <td className="sticky left-0 bg-white px-3 py-2 font-medium dark:bg-gray-900">
          <button type="button" aria-expanded={expanded} onClick={onExpand} className="mr-1 text-gray-500" aria-label="Show the file rows">
            {expanded ? '▾' : '▸'}
          </button>
          {line.policy ? (
            <Link className="text-primary-600 underline" to={`/policies/${line.policy}`}>
              {line.base_policy_number}
            </Link>
          ) : (
            line.base_policy_number
          )}
          {line.row_count > 1 && <Badge size="sm" className="ml-1">{line.row_count} rows</Badge>}
        </td>
        <td className="px-3 py-2">
          <Badge variant={CATEGORY_VARIANT[line.category]} size="sm">
            {CATEGORY_LABELS[line.category]}
          </Badge>
        </td>
        <td className="px-3 py-2 text-right">
          {money(line.stated_premium)} / {money(line.expected_premium)}
        </td>
        <td className="px-3 py-2">
          <RateCell line={line} />
        </td>
        <td className="px-3 py-2 text-right">{money(line.stated_total)}</td>
        <td className="px-3 py-2 text-right">{money(line.expected_total)}</td>
        <td className={`px-3 py-2 text-right font-medium ${diffClass(line.difference)}`}>{money(line.difference)}</td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap gap-1">
            {line.flags.map((f) => (
              <Badge key={f} variant="warning" size="sm">
                {KIND_BADGE[f] ?? flagLabel(f)}
              </Badge>
            ))}
          </div>
        </td>
        <td className="px-3 py-2">{line.decision && line.decision !== 'none' ? DECISION_LABELS[line.decision] : '—'}</td>
        <td className="px-3 py-2">{line.dispute ? DISPUTE_LABELS[line.dispute.status] : '—'}</td>
        <td className="px-3 py-2 text-right">
          <Button size="sm" variant="outline" onClick={onOpen}>
            Details
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={cols} className="bg-gray-50 px-6 py-2 dark:bg-gray-800">
            <NettedRows id={line.id} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Entry ─────────────────────────────────────────────────────────────────────

export function StatementsView({ canAct, onUpload }: { canAct: boolean; onUpload: () => void }) {
  const [params, setParams] = useSearchParams();
  const batchId = params.get('stmt');

  const batch = useQuery({
    queryKey: ['statementBatch', batchId],
    queryFn: () => statementVerificationService.getBatch(batchId!),
    enabled: Boolean(batchId),
  });

  const open = (b: StatementBatch) => {
    const next = new URLSearchParams(params);
    next.set('stmt', b.id);
    setParams(next);
  };
  const back = () => {
    const next = new URLSearchParams(params);
    ['stmt', 'cat', 'flag', 'dec', 'disp', 'q', 'sort', 'page'].forEach((k) => next.delete(k));
    setParams(next);
  };

  if (!batchId) return <StatementsList canAct={canAct} onOpen={open} onUpload={onUpload} />;
  if (batch.isLoading) return <Skeleton />;
  if (batch.isError || !batch.data) {
    return (
      <div className="space-y-3">
        <ErrorBox error={batch.error ?? new Error('Statement not found')} onRetry={() => batch.refetch()} />
        <Button variant="ghost" size="sm" onClick={back}>
          Back to statements
        </Button>
      </div>
    );
  }
  return <StatementView batch={batch.data} canAct={canAct} onBack={back} onUpload={onUpload} />;
}
